/*

A general purpose fetch function for ARM resources. Not designed to be used directly
Instead, generate ARM clients that consume this function with stricter typing.

*/
import promiseRetry, { AbortError } from "p-retry";
import { HttpHeaders } from "../../Common/Constants";
import { configContext } from "../../ConfigContext";
import { userContext } from "../../UserContext";
import { fetchWithTimeout } from "../FetchWithTimeout";
export class ARMError extends Error {
    constructor(message) {
        super(message);
        // Set the prototype explicitly.
        // https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
        Object.setPrototypeOf(this, ARMError.prototype);
    }
}
const DEFAULT_ARM_TIMEOUT_MS = 5000;
const isAbortError = (error) => error instanceof Error && error.name === "AbortError";
export async function armRequestWithoutPolling({ host, path, apiVersion, method, body: requestBody, queryParams, contentType, customHeaders, signal, timeoutMs, }) {
    const url = new URL(path, host);
    url.searchParams.append("api-version", configContext.armAPIVersion || apiVersion);
    if (queryParams) {
        queryParams.filter && url.searchParams.append("$filter", queryParams.filter);
        queryParams.metricNames && url.searchParams.append("metricnames", queryParams.metricNames);
    }
    if (!(userContext === null || userContext === void 0 ? void 0 : userContext.authorizationToken) && !(customHeaders === null || customHeaders === void 0 ? void 0 : customHeaders["Authorization"])) {
        throw new Error("No authority token provided");
    }
    const headers = Object.assign({ Authorization: userContext.authorizationToken || (customHeaders === null || customHeaders === void 0 ? void 0 : customHeaders["Authorization"]) || "", [HttpHeaders.contentType]: contentType || "application/json" }, (customHeaders || {}));
    const fetchInit = {
        method,
        headers,
        body: requestBody ? JSON.stringify(requestBody) : undefined,
        signal,
    };
    const effectiveTimeoutMs = timeoutMs !== null && timeoutMs !== void 0 ? timeoutMs : DEFAULT_ARM_TIMEOUT_MS;
    const response = await fetchWithRetry(url.href, fetchInit, method, effectiveTimeoutMs, signal);
    if (!response.ok) {
        let error;
        try {
            const errorResponse = (await response.json());
            if ("error" in errorResponse) {
                error = new ARMError(errorResponse.error.message);
                error.code = errorResponse.error.code;
            }
            else {
                error = new ARMError(errorResponse.message);
                error.code = errorResponse.code;
            }
        }
        catch (error) {
            throw new Error(await response.text());
        }
        throw error;
    }
    const operationStatusUrl = (response.headers && response.headers.get("location")) || "";
    const responseBody = (await response.json());
    return { result: responseBody, operationStatusUrl: operationStatusUrl };
}
// TODO: This is very similar to what is happening in ResourceProviderClient.ts. Should probably merge them.
export async function armRequest({ host, path, apiVersion, method, body: requestBody, queryParams, contentType, customHeaders, signal, timeoutMs, }) {
    const armRequestResult = await armRequestWithoutPolling({
        host,
        path,
        apiVersion,
        method,
        body: requestBody,
        queryParams,
        contentType,
        customHeaders,
        signal,
        timeoutMs,
    });
    const operationStatusUrl = armRequestResult.operationStatusUrl;
    if (operationStatusUrl) {
        return await promiseRetry(() => getOperationStatus(operationStatusUrl));
    }
    return armRequestResult.result;
}
/**
 * Calls `fetchWithTimeout` once for non-idempotent methods. For idempotent GETs, retries
 * up to {@link RETRY_TIMEOUT_MULTIPLIERS}.length attempts on timeout, escalating the timeout
 * on each attempt. HTTP error responses (4xx/5xx) are NOT retried — they are surfaced by the
 * caller's response.ok check. External `signal` cancellation aborts the retry loop immediately.
 */
async function fetchWithRetry(url, fetchInit, method, timeoutMs, signal) {
    if (method !== "GET") {
        return fetchWithTimeout(url, fetchInit, timeoutMs);
    }
    const RETRY_TIMEOUT_MULTIPLIERS = [1, 2, 4];
    const RETRY_BACKOFF_MS = 100;
    return promiseRetry((attemptNumber) => {
        const attemptTimeoutMs = timeoutMs * RETRY_TIMEOUT_MULTIPLIERS[Math.min(attemptNumber - 1, RETRY_TIMEOUT_MULTIPLIERS.length - 1)];
        return fetchWithTimeout(url, fetchInit, attemptTimeoutMs);
    }, {
        retries: RETRY_TIMEOUT_MULTIPLIERS.length - 1,
        factor: 1,
        minTimeout: RETRY_BACKOFF_MS,
        maxTimeout: RETRY_BACKOFF_MS,
        signal,
        // Only retry on timeout aborts. Caller cancellation (external signal aborted) stops retries.
        shouldRetry: (error) => isAbortError(error) && !(signal === null || signal === void 0 ? void 0 : signal.aborted),
    });
}
async function getOperationStatus(operationStatusUrl) {
    if (!userContext.authorizationToken) {
        throw new Error("No authority token provided");
    }
    const response = await window.fetch(operationStatusUrl, {
        headers: {
            Authorization: userContext.authorizationToken,
        },
    });
    if (!response.ok) {
        const errorResponse = (await response.json());
        const error = new Error(errorResponse.message);
        error.code = errorResponse.code;
        throw new AbortError(error);
    }
    if (response.status === 204) {
        return;
    }
    const body = await response.json();
    const status = body.status;
    if (status === "Canceled" || status === "Failed") {
        const errorMessage = body.error ? JSON.stringify(body.error) : "Operation could not be completed";
        const error = new Error(errorMessage);
        throw new AbortError(error);
    }
    if (response.status === 200) {
        return body;
    }
    throw new Error(`Operation Response: ${JSON.stringify(body)}. Retrying.`);
}
export async function getOfferingIdsRequest({ host, path, apiVersion, method, body: requestBody, queryParams, }) {
    const url = new URL(path, host);
    url.searchParams.append("api-version", configContext.armAPIVersion || apiVersion);
    if (queryParams) {
        queryParams.filter && url.searchParams.append("$filter", queryParams.filter);
        queryParams.metricNames && url.searchParams.append("metricnames", queryParams.metricNames);
    }
    if (!configContext.CATALOG_API_KEY) {
        throw new Error("No catalog API key provided");
    }
    const response = await window.fetch(url.href, {
        method,
        headers: {
            [HttpHeaders.xAPIKey]: configContext.CATALOG_API_KEY,
        },
        body: requestBody ? JSON.stringify(requestBody) : undefined,
    });
    if (!response.ok) {
        let error;
        try {
            const errorResponse = (await response.json());
            if ("error" in errorResponse) {
                error = new ARMError(errorResponse.error.message);
                error.code = errorResponse.error.code;
            }
            else {
                error = new ARMError(errorResponse.message);
                error.code = errorResponse.code;
            }
        }
        catch (error) {
            throw new Error(await response.text());
        }
        throw error;
    }
    const operationStatusUrl = (response.headers && response.headers.get("location")) || "";
    const responseBody = (await response.json());
    return { result: responseBody, operationStatusUrl: operationStatusUrl };
}
